import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { FieldLabel } from '@/components/FieldLabel'
import { cn } from '@/lib/utils'

interface OptionalFieldProps {
  /** Label of the field once it is shown. */
  label: string
  /** Explanatory help, hidden behind a hover icon next to the label. */
  tooltip?: string
  /** Text of the button that reveals the field, e.g. "Add deal ID". */
  addLabel: string
  /** A field that holds a value is always shown, never behind the button. */
  hasValue: boolean
  /** Clears the value — the field is hidden again once it is empty. */
  onClear: () => void
  children: ReactNode
  className?: string
}

/**
 * A field that stays out of the way until it is needed: it shows as an "add"
 * button while empty, and as a normal `.p-field` — with a button to clear it
 * again — once it has been added or already holds a value.
 */
export function OptionalField({
  label,
  tooltip,
  addLabel,
  hasValue,
  onClear,
  children,
  className,
}: OptionalFieldProps) {
  const { t } = useTranslation()
  const [added, setAdded] = useState(false)

  if (!hasValue && !added) {
    return (
      <div className={className}>
        <Button type="button" variant="ghost" size="sm" onClick={() => setAdded(true)}>
          <Plus className="size-3.5" />
          {addLabel}
        </Button>
      </div>
    )
  }

  return (
    <div className={cn('p-field', className)}>
      <div className="flex items-center justify-between gap-2">
        <FieldLabel tooltip={tooltip}>{label}</FieldLabel>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={t('common.remove')}
          onClick={() => {
            onClear()
            setAdded(false)
          }}
        >
          <Trash2 className="size-3.5" />
        </Button>
      </div>
      {children}
    </div>
  )
}
